import { __decorate } from "tslib";
import React from 'react';
import { observer } from 'mobx-react';
import { action, observable, runInAction } from 'mobx';
import isString from 'lodash/isString';
import ReactResizeObserver from 'choerodon-ui/lib/_util/resizeObserver';
import { TextField } from '../text-field/TextField';
import { ResizeType } from './enum';
import calculateNodeHeight from './calculateNodeHeight';
import autobind from '../_util/autobind';
function getResizeProp(resize) {
    switch (resize) {
        case ResizeType.both:
            return 'both';
        case ResizeType.vertical:
            return 'height';
        case ResizeType.horizontal:
            return 'width';
        default:
            return undefined;
    }
}
let TextArea = class TextArea extends TextField {
    get multiple() {
        return false;
    }
    get resize() {
        const { autoSize, resize } = this.props;
        if (autoSize) {
            const { minRows, maxRows } = autoSize;
            if (minRows && minRows === maxRows) {
                return undefined;
            }
        }
        return resize;
    }
    get autoSize() {
        const { autoSize } = this.props;
        if (autoSize) {
            const { minRows, maxRows } = autoSize;
            if (minRows && minRows === maxRows) {
                return false;
            }
        }
        return autoSize;
    }
    get rows() {
        const { rows = 4, autoSize } = this.props;
        if (autoSize) {
            const { minRows } = autoSize;
            return minRows || rows;
        }
        return rows;
    }
    componentDidMount() {
        super.componentDidMount();
        if (this.element && this.autoSize) {
            // 自适应高度，挂载时渲染样式
            this.forceUpdate();
        }
    }
    componentDidUpdate() {
        const { element, autoSize } = this;
        if (autoSize && element) {
            const { minRows, maxRows } = autoSize;
            const calculateStyle = calculateNodeHeight(element, true, minRows, maxRows);
            const { autoSizeCalcHeight } = this;
            if (!autoSizeCalcHeight || calculateStyle.height !== autoSizeCalcHeight.height) {
                runInAction(() => {
                    this.autoSizeCalcHeight = calculateStyle;
                });
            }
        }
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'resize',
            'autoSize',
            'onResize',
        ]);
    }
    getOtherProps() {
        const { resize = ResizeType.none, autoSize, rows } = this;
        const otherProps = super.getOtherProps();
        const { style = {} } = otherProps;
        style.resize = resize;
        if (resize !== ResizeType.none) {
            style.transition = 'none';
        }
        if (autoSize) {
            const { autoSizeCalcHeight } = this;
            if (autoSizeCalcHeight) {
                Object.assign(style, autoSizeCalcHeight);
            }
        }
        otherProps.rows = rows;
        otherProps.style = style;
        return otherProps;
    }
    handleResize(width, height, target) {
        const { onResize } = this.props;
        if (!this.resized) {
            const { element } = this;
            if (element && element.style.width) {
                this.resized = true;
            }
        }
        if (onResize) {
            onResize(width, height, target);
        }
    }
    getSuffix() {
        return null;
    }
    renderLengthInfoWrapper() {
        if (!this.showLengthInfo) {
            return null;
        }
        const editorTextInfo = this.getEditorTextInfo();
        const inputLength = editorTextInfo.text.length;
        const maxLength = this.getProp('maxLength');
        const lengthElement = this.renderLengthInfo(maxLength, inputLength);
        return lengthElement;
    }
    handleClearButtonClick(e) {
        e.preventDefault();
        this.clear();
    }
    handleInnerButtonMouseDown(e) {
        e.preventDefault();
    }
    renderWrapper() {
        const { resize = ResizeType.none } = this;
        const resizable = resize !== ResizeType.none;
        const wrapperProps = this.getWrapperProps() || {};
        const lengthElement = this.renderLengthInfoWrapper();
        // 先计算suffix，然后再计算clearButton，设置right，避免重叠
        const suffix = this.getSuffix();
        const button = this.getInnerSpanButton();
        if (this.resized) {
            const { style: wrapperStyle } = wrapperProps;
            wrapperProps.style = {
                ...wrapperStyle,
                width: 'auto',
            };
        }
        if (lengthElement) {
            const { style: wrapperStyle } = wrapperProps;
            wrapperProps.style = {
                ...wrapperStyle,
                marginBottom: '0.2rem',
            };
        }
        const renderedValue = this.renderRenderedValue(undefined, { className: `${this.prefixCls}-renderer-wrapper` });
        const element = this.wrapperInputNode(renderedValue);
        const children = (React.createElement(React.Fragment, null,
            element,
            lengthElement,
            suffix,
            button));
        return (React.createElement("div", Object.assign({ key: "wrapper" }, wrapperProps),
            this.renderPlaceHolder(),
            renderedValue,
            React.createElement("label", null,
                resizable ? (React.createElement(ReactResizeObserver, { onResize: this.handleResize, resizeProp: getResizeProp(resize) }, children)) : children,
                this.renderFloatLabel())));
    }
    wrapperInputNode(renderedValue) {
        const text = this.getTextNode();
        const elementProps = this.getOtherProps() || {};
        const otherProps = {};
        if (renderedValue) {
            otherProps.style = {
                ...elementProps.style,
                textIndent: -1000,
                color: 'transparent',
            };
        }
        return (React.createElement("textarea", Object.assign({}, elementProps, otherProps, { placeholder: this.hasFloatLabel && !this.isFocused ? undefined : this.getPlaceholders()[0], readOnly: !this.editable, value: isString(text) ? text : this.processValue(this.getValue()) })));
    }
    handleEnterDown(_) {
        // noop
    }
};
TextArea.displayName = 'TextArea';
TextArea.defaultProps = {
    ...TextField.defaultProps,
    suffixCls: 'textarea',
    autoSize: false,
};
// eslint-disable-next-line camelcase
TextArea.__PRO_TEXTAREA = true;
__decorate([
    observable
], TextArea.prototype, "resized", void 0);
__decorate([
    observable
], TextArea.prototype, "autoSizeCalcHeight", void 0);
__decorate([
    autobind,
    action
], TextArea.prototype, "handleResize", null);
__decorate([
    autobind
], TextArea.prototype, "handleClearButtonClick", null);
TextArea = __decorate([
    observer
], TextArea);
export default TextArea;
